// ✅ src/screens/LandingScreen.js (CRA / React Router)
// - NO next/link
// - NO styled-jsx
// - Uses App.css classes
// - ✅ Always show: Log in + Sign up
// - ✅ Routes to /login (matches App.js)

import React from "react";
import { Link } from "react-router-dom";

export default function LandingScreen() {
  return (
    <main className="lp">
      {/* Top Nav */}
      <header className="lp-nav" id="top">
        <div className="lp-brand">
          <img className="lp-logo" src="/images/logo.png" alt="Logo" />
          <span className="lp-brandName">GhanaDating</span>
          <span className="pill">Adults 18+</span>
        </div>

        <nav className="lp-links">
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#meetups">Meetups</a>
          <a href="#safety">Safety</a>
        </nav>

        <div className="lp-cta">
          <Link className="btn btn-ghost" to="/login">
            Log in
          </Link>
          <Link className="btn btn-primary" to="/login">
            Sign up
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="hero-left">
          <div className="tag">🇬🇭 Made for Ghana • Real people • Nightlife friendly ✨</div>

          <h1>
            Meet <span className="accent">local singles</span>, vibe with
            <br />
            real chemistry.
          </h1>

          <p className="sub">
            A classy dating + meetup space for Ghanaian adults. Match, chat, plan a date — keep
            it private, keep it fun.
          </p>

          <div className="chips">
            <span className="chip">✅ Verified option</span>
            <span className="chip">🔒 Private chats</span>
            <span className="chip">📍 Nearby matches</span>
            <span className="chip">🔥 Flirty vibes</span>
          </div>

          <div className="hero-actions">
            <Link className="btn btn-primary btn-big" to="/login">
              Start matching
            </Link>
            <Link className="btn btn-ghost btn-big" to="/login">
              Log in / Sign up
            </Link>

            <a className="btn btn-ghost btn-big" href="#how">
              See how it works
            </a>
          </div>

          <div className="fineprint">
            Adults only (18+). Keep it respectful. You control your experience.
          </div>

          <div className="proof">
            <div className="proof-avatars">
              <img src="/images/profile-woman-1.jpg" alt="Member" />
              <img src="/images/profile-man-1.jpg" alt="Member" />
              <img src="/images/profile-woman-1.jpg" alt="Member" />
            </div>
            <div className="proof-text">
              <strong>Thousands</strong> of new connections every week.
              <div className="muted small">Accra • Kumasi • Takoradi • Tamale</div>
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="hero-card">
            <div className="hero-card-top">
              <span className="dot dot-red" />
              <span className="dot dot-yellow" />
              <span className="dot dot-green" />
              <span className="muted small">Live preview</span>
            </div>

            <div className="mock">
              <div className="mock-imgWrap">
                <img className="mock-img" src="/images/hero-couple.jpg" alt="Couple" />
                <div className="mock-badge">Flirty • Private • Ghana 🇬🇭</div>
              </div>

              <div className="mock-body">
                <div className="mock-title">Ready for a real vibe?</div>
                <div className="mock-sub">Adults only (18+). Match, chat, and plan meetups.</div>

                <div className="mock-actions">
                  <Link className="btn btn-primary w100" to="/login">
                    Join free
                  </Link>
                  <Link className="btn btn-ghost w100" to="/login">
                    Log in
                  </Link>
                </div>

                <div className="small muted center mt8">
                  You can change privacy + safety settings anytime.
                </div>
              </div>
            </div>
          </div>

          <div className="side-note">🔥 Optional “Spicy Mode” filters for adults — still respectful.</div>
        </div>
      </section>

      {/* Features */}
      <section className="section" id="features">
        <h2>Everything you need to connect</h2>
        <p className="muted center maxw">
          Built for speed, safety, and real conversations — plus meetups when you’re ready.
        </p>

        <div className="grid3">
          <div className="card">
            <div className="icon">💘</div>
            <h3>Smart matching</h3>
            <p className="muted">
              Filter by city, age range, intent (dating / meetups), and preferences. Find people near you.
            </p>
          </div>

          <div className="card">
            <div className="icon">💬</div>
            <h3>Fast chat</h3>
            <p className="muted">
              Real-time messaging with clean UI. Icebreakers included so you don’t start with “hi”.
            </p>
          </div>

          <div className="card">
            <div className="icon">📍</div>
            <h3>Meetups & dates</h3>
            <p className="muted">
              Plan a safe meetup: coffee, chill spots, events. Share location only when you want.
            </p>
          </div>
        </div>

        <div className="grid3 mt">
          <div className="card glass">
            <h3>🔎 “Intent” profiles</h3>
            <p className="muted">Choose your vibe: Dating, Friendship, or Meetups. No confusion.</p>
          </div>
          <div className="card glass">
            <h3>✨ Verified option</h3>
            <p className="muted">Verify for serious matches. Keep it optional for privacy.</p>
          </div>
          <div className="card glass">
            <h3>🔥 Adult filters (safe)</h3>
            <p className="muted">Flirty settings for grown folks — without explicit content.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section alt" id="how">
        <h2>How it works</h2>

        <div className="steps">
          <div className="step">
            <div className="num">1</div>
            <div>
              <h3>Create your profile</h3>
              <p className="muted">Add photos, city, intent, and a short bio. Keep it confident.</p>
            </div>
          </div>

          <div className="step">
            <div className="num">2</div>
            <div>
              <h3>Browse & like</h3>
              <p className="muted">See people in your area. Like to connect. Pass to skip.</p>
            </div>
          </div>

          <div className="step">
            <div className="num">3</div>
            <div>
              <h3>Match, chat, meetup</h3>
              <p className="muted">
                When it’s mutual, chat instantly — and plan a safe meetup when you’re ready.
              </p>
            </div>
          </div>
        </div>

        <div className="ctaRow">
          <Link className="btn btn-primary btn-big" to="/login">
            Start matching
          </Link>
          <a className="btn btn-ghost btn-big" href="#safety">
            See safety tools
          </a>
        </div>
      </section>

      {/* Meetups */}
      <section className="section" id="meetups">
        <h2>Meetup ideas (Ghana vibes)</h2>
        <p className="muted center maxw">
          Plan something simple and safe. You can keep it private or go public.
        </p>

        <div className="grid3">
          <div className="card">
            <h3>☕ Coffee / brunch</h3>
            <p className="muted">Quick meetups in public. Perfect for first vibes.</p>
          </div>
          <div className="card">
            <h3>🎶 Events / chill spots</h3>
            <p className="muted">Link up at concerts, lounges, or weekend hangouts.</p>
          </div>
          <div className="card">
            <h3>🌅 Evening date</h3>
            <p className="muted">For grown adults. Slow conversations. Real chemistry.</p>
          </div>
        </div>

        <div className="ctaRow">
          <Link className="btn btn-primary btn-big" to="/login">
            Join & explore
          </Link>
          <a className="btn btn-ghost btn-big" href="#features">
            View features
          </a>
        </div>
      </section>

      {/* Safety */}
      <section className="section" id="safety">
        <h2>Safety controls</h2>
        <p className="muted center maxw">You control your experience. Block, report, hide users — anytime.</p>

        <div className="grid2">
          <div className="card">
            <h3>Block instantly</h3>
            <p className="muted">Don’t like the vibe? Block them. They won’t see you again.</p>
          </div>
          <div className="card">
            <h3>Report bad behavior</h3>
            <p className="muted">Report spam, scams, harassment. Moderation queue handles it.</p>
          </div>
        </div>

        <div className="grid2 mt">
          <div className="card glass">
            <h3>🔐 Privacy controls</h3>
            <p className="muted">
              Hide your profile from searches, limit who can message, and control what you share.
            </p>
          </div>
          <div className="card glass">
            <h3>✅ Verification (optional)</h3>
            <p className="muted">Optional verification to reduce fake accounts and scammers.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-left">
            <div className="lp-brand">
              <img className="lp-logo" src="/images/logo.png" alt="Logo" />
              <span className="lp-brandName">GhanaDating</span>
              <span className="pill">18+</span>
            </div>
            <div className="muted small">
              © {new Date().getFullYear()} GhanaDating. All rights reserved.
              <span className="dotsep">•</span>
              Adults only (18+).
            </div>
          </div>

          <div className="footer-links">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            {/* If you don't have /contact route yet, remove this */}
            {/* <Link to="/contact">Contact</Link> */}
          </div>
        </div>
      </footer>
    </main>
  );
}
